import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Sun, Sparkles, BookOpen, Calculator, Pencil, CheckCircle2, ArrowRight, Trophy, Flame } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BeatTheSummerSlidePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('landing'); // landing → form → plan
  const [form, setForm] = useState({
    child_name: '',
    age: 7,
    grade: '2',
    interests: '',
  });
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const generatePlan = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/summer-slide/generate-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          child_name: form.child_name || undefined,
          age: parseInt(form.age),
          grade: form.grade,
          subjects: ['reading', 'math', 'writing'],
          interests: form.interests || undefined,
          weeks: 8,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate plan');
      const data = await res.json();
      setPlan(data.plan);
      setStep('plan');
    } catch (err) {
      toast.error(err.message || 'Failed to generate plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============ LANDING ============
  if (step === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-white">
        <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-amber-100">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">Z</span>
              </div>
              <span className="font-bold text-slate-900">Zazu</span>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm">Sign in</Button>
            </Link>
          </div>
        </nav>

        <main className="max-w-6xl mx-auto px-4 py-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Sun className="w-4 h-4" /> Summer 2026
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6">
              Beat the <span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent">Summer Slide</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-8">
              Kids lose <strong>2-3 months</strong> of learning every summer. Zazu builds them a personalized,
              AI-powered summer plan — just 15 minutes a day keeps skills sharp.
            </p>
            <Button
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-8 h-14 shadow-lg"
              onClick={() => setStep('form')}
              data-testid="cta-generate-plan"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Get Your Free AI Summer Plan
            </Button>
            <p className="text-xs text-slate-500 mt-4">Free forever • No credit card • Instant plan in 20 seconds</p>
          </div>

          {/* Feature grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Sparkles, color: 'from-purple-400 to-indigo-500',
                title: 'AI-Personalized',
                body: 'Every plan is custom-generated for your child’s exact age, grade, and interests.'
              },
              {
                icon: Trophy, color: 'from-amber-400 to-orange-500',
                title: '15 Min. Daily',
                body: 'Bite-sized activities across reading, math, and writing. Fun, not homework.'
              },
              {
                icon: Flame, color: 'from-rose-400 to-pink-500',
                title: 'Streak Rewards',
                body: 'Kids earn Summer Streak badges every week. Motivation, gamified.'
              },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center mb-4`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-lg mb-2">{f.title}</h3>
                    <p className="text-slate-600 text-sm">{f.body}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Weekly themes preview */}
          <Card className="border-amber-200 bg-white shadow-lg mb-16">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">8 Weeks of Summer Themes</h2>
              <p className="text-slate-600 mb-6">Each week has a fresh theme kids can’t wait to open.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['🏖️ Beach & Ocean','🌳 Backyard Adventure','⛺ Camping','⚽ Sports & Games','✈️ Family Travel','🌸 Garden & Nature','🍰 Cooking & Baking','🚀 Stars & Space'].map((t, i) => (
                  <div key={t} className="p-4 rounded-xl bg-amber-50 border border-amber-100 text-center">
                    <p className="text-xs text-amber-700 font-semibold mb-1">Week {i + 1}</p>
                    <p className="text-sm text-slate-800 font-medium">{t}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bottom CTA */}
          <div className="text-center bg-gradient-to-r from-amber-500 to-orange-600 rounded-3xl p-10 text-white">
            <h2 className="text-3xl font-extrabold mb-3">Ready to save your child’s summer?</h2>
            <p className="text-amber-50 mb-6 max-w-xl mx-auto">
              20 seconds to generate. 15 minutes a day to keep every skill sharp.
            </p>
            <Button
              size="lg"
              className="bg-white text-orange-600 hover:bg-amber-50 text-lg px-8 h-14"
              onClick={() => setStep('form')}
              data-testid="cta-generate-plan-2"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Get My Free Plan
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // ============ FORM ============
  if (step === 'form') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full shadow-xl">
          <CardContent className="p-8">
            <div className="mb-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-3">
                <Sun className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Tell us about your child</h2>
              <p className="text-sm text-slate-600 mt-1">Our AI creates a plan tailored to them.</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Child’s first name (optional)</Label>
                <Input
                  id="name" value={form.child_name}
                  onChange={(e) => setForm({ ...form, child_name: e.target.value })}
                  placeholder="e.g. Alex"
                  data-testid="form-child-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="age">Age</Label>
                  <Input
                    id="age" type="number" min="3" max="14"
                    value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })}
                    data-testid="form-age"
                  />
                </div>
                <div>
                  <Label htmlFor="grade">Grade (fall)</Label>
                  <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                    <SelectTrigger data-testid="form-grade"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['K', '1', '2', '3', '4', '5', '6', '7', '8'].map((g) => (
                        <SelectItem key={g} value={g}>{g === 'K' ? 'Kindergarten' : `Grade ${g}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="interests">Interests (optional)</Label>
                <Input
                  id="interests" value={form.interests}
                  onChange={(e) => setForm({ ...form, interests: e.target.value })}
                  placeholder="e.g. dinosaurs, space, soccer"
                  data-testid="form-interests"
                />
              </div>
            </div>

            <Button
              className="w-full bg-amber-500 hover:bg-amber-600 mt-6 h-12"
              onClick={generatePlan}
              disabled={loading}
              data-testid="submit-generate"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating your plan…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Generate My Plan</>
              )}
            </Button>
            <Button variant="ghost" className="w-full mt-2" onClick={() => setStep('landing')}>← Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ PLAN RESULT ============
  const SUBJECT_META = {
    reading: { icon: BookOpen, color: 'bg-rose-100 text-rose-700', label: 'Reading' },
    math: { icon: Calculator, color: 'bg-amber-100 text-amber-700', label: 'Math' },
    writing: { icon: Pencil, color: 'bg-sky-100 text-sky-700', label: 'Writing' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white pb-16">
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-amber-100">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun className="w-5 h-5 text-amber-500" />
            <span className="font-bold text-slate-900">Your Summer Plan</span>
          </div>
          <Button
            className="bg-teal-700 hover:bg-teal-800 text-white"
            onClick={() => navigate('/register')}
            data-testid="save-plan-signup"
          >
            Save my plan — Sign up free
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8 p-8 rounded-3xl bg-gradient-to-r from-amber-500 to-orange-600 text-white">
          <p className="text-sm uppercase tracking-wider text-amber-100">Personalized for {form.child_name || 'your child'}</p>
          <h1 className="text-3xl font-extrabold mt-1 mb-3">
            {plan?.weeks?.length || 8}-Week Summer Learning Plan
          </h1>
          <p className="text-amber-50 max-w-xl">{plan?.overview}</p>
        </div>

        <div className="space-y-8">
          {(plan?.weeks || []).map((week) => (
            <Card key={week.week} className="border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-100 to-orange-100 px-6 py-4 border-b border-amber-200">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Week {week.week}</p>
                    <h3 className="text-xl font-bold text-slate-900">{week.theme}</h3>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(week.focus_skills || []).map((s) => (
                      <Badge key={s} variant="outline" className="bg-white/60">{s}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <CardContent className="p-6 space-y-4">
                {(week.days || []).map((day) => (
                  <div key={day.day} className="border-l-4 border-amber-400 pl-4">
                    <h4 className="font-bold text-slate-800 mb-2">{day.day_name || `Day ${day.day}`}</h4>
                    <div className="grid md:grid-cols-3 gap-3">
                      {(day.activities || []).map((act, i) => {
                        const meta = SUBJECT_META[act.subject] || SUBJECT_META.reading;
                        const Icon = meta.icon;
                        return (
                          <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-7 h-7 rounded-lg ${meta.color} flex items-center justify-center`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <span className="text-xs font-semibold text-slate-600">{meta.label}</span>
                            </div>
                            <p className="font-medium text-slate-900 text-sm mb-1">{act.title}</p>
                            <p className="text-xs text-slate-600 leading-relaxed">{act.description}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center p-8 bg-white border-2 border-amber-300 rounded-3xl shadow-lg">
          <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-teal-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Ready to unlock everything?</h2>
          <p className="text-slate-600 mb-6 max-w-xl mx-auto">
            Save this plan, track your child’s Summer Streak, and get AI voice narration, gamification, and daily reminders — free.
          </p>
          <Button
            size="lg"
            className="bg-teal-700 hover:bg-teal-800 text-white text-lg px-8 h-14"
            onClick={() => navigate('/register')}
            data-testid="final-cta"
          >
            Create Free Account
            <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </main>
    </div>
  );
};

export default BeatTheSummerSlidePage;
